import React from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { Card, Image } from 'react-bootstrap';
import '../components/css/ContentBlock.css';

const ContentBlock = ({ block }) => {
  if (!block) return null;

  // Render code block with syntax highlighting
  const renderCode = (code, language = 'javascript') => {
    return (
      <div className="code-block-wrapper">
        <div className="code-header">
          <span className="language-tag">{language}</span>
          <button 
            className="copy-button"
            onClick={() => navigator.clipboard.writeText(code)}
          >
            Copy
          </button>
        </div>
        <SyntaxHighlighter
          language={language}
          style={vs2015}
          customStyle={{
            margin: 0,
            borderRadius: '0 0 8px 8px',
            padding: '1.5rem',
            fontSize: '0.9rem'
          }}
          showLineNumbers={true}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    );
  };

  // Safely split text into list items
  const renderListItems = (text) => {
    if (!text) return [];
    return text.split('\n').filter(item => item.trim() !== '');
  };

  try {
    switch (block.blockType) {
      case 'heading':
        return (
          <>
            {block.image === 'h1' ? (
              <h1 className="content-heading my-3">
                {block.text || 'Untitled Heading'}
              </h1>
            ) : block.image === 'h2' ? (
              <h2 className="content-heading my-3">
                {block.text || 'Untitled Heading'}
              </h2>
            ) : block.image === 'h3' ? (
              <h3 className="content-heading my-3">
                {block.text || 'Untitled Heading'}
              </h3>
            ) : block.image === 'h4' ? (
              <h4 className="content-heading my-3">
                {block.text || 'Untitled Heading'}
              </h4>
            ) : (
              <h3 className="content-heading my-3">
                {block.text || 'Untitled Heading'}
              </h3>
            )}
          </>
        );
      
      case 'text':
        return (
          <div className="content-text">
            {block.text || ''}
          </div>
        );
        case 'html':
          return (
            <div
              className="content-html"
              dangerouslySetInnerHTML={{
                __html: block.text || ''
              }}
            />
          );
      
          case 'image':
            if (!block.image) {
              return (
                <div className="image-placeholder my-3 p-3 bg-light text-center">
                  [Image not available]
                </div>
              );
            }
            
            return (
              <figure className="content-figure my-3">
                <div className="image-wrapper text-center">
                  <img
                    src={block.image}
                    alt={block.text || 'Content image'}
                    className="content-image img-fluid rounded"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none'; // Instantly hide broken image
                    }}
                  />
                </div>
                {block.text && (
                  <figcaption className="image-caption text-center mt-2 text-muted">
                    {block.text}
                  </figcaption>
                )}
              </figure>
            );   
            
            
      case 'code':
        if (!block.text) return null;
        return renderCode(block.text, block.image || 'javascript');
      
      case 'quote':
        return (
          <blockquote className="content-quote my-3 p-3 bg-light border-start border-4">
            <p className="mb-0">{block.text || ''}</p>
            {block.image && (
              <footer className="quote-author mt-2 text-end">
                — {block.image}
              </footer>
            )}
          </blockquote>
        );

      case 'list':
        const items = renderListItems(block.text);
        if (items.length === 0) return null;
        
        const isOrdered = block.image === 'ordered';
        const ListTag = isOrdered ? 'ol' : 'ul';
        
        return (
          <ListTag className="content-list my-3">
            {items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ListTag>
        );
      
        case 'youtube':
          if (!block.text) return null;
          
          // Extract YouTube video ID
          const getYouTubeId = (url) => {
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
          };
          
          const videoId = getYouTubeId(block.text);
          if (!videoId) return null;
        
          // Autoplay settings
          const autoplayParam = block.image === 'autoplay' ? "&autoplay=1&mute=1" : "";
          const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0${autoplayParam}`;
        
          return (
            <div className="youtube-embed my-4 ratio ratio-16x9">
              <iframe
                src={embedUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="YouTube video player"
              ></iframe>
            </div>
          );

      default:
        return (
          <div className="content-unknown my-3 p-3 bg-warning bg-opacity-10">
            [Unsupported content type: {block.blockType}]
          </div>
        );
    }
  } catch (error) {
    console.error('Error rendering content block:', error);
    return (
      <div className="content-error my-3 p-3 bg-danger bg-opacity-10">
        [Error displaying this content]
      </div>
    );
  }
};

export default ContentBlock;